import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

async function fetchMe() {
    const { data } = await api.get("/auth/me");
    return data;
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = unknown/loading
    const [checked, setChecked] = useState(false);

    // Initial auth check on mount — cancellation-safe pattern avoids
    // setState-after-unmount and satisfies react-hooks/set-state-in-effect.
    useEffect(() => {
        let cancelled = false;
        fetchMe()
            .then((data) => {
                if (!cancelled) setUser(data);
            })
            .catch((err) => {
                if (cancelled) return;
                if (err?.response?.status && err.response.status !== 401) {
                    console.error("[Auth] /me failed:", err);
                }
                setUser(false);
            })
            .finally(() => {
                if (!cancelled) setChecked(true);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const refresh = useCallback(async () => {
        try {
            const data = await fetchMe();
            setUser(data);
        } catch (err) {
            if (err?.response?.status && err.response.status !== 401) {
                console.error("[Auth] /me failed:", err);
            }
            setUser(false);
        }
    }, []);

    const login = useCallback(async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        setUser(data);
        return data;
    }, []);

    const register = useCallback(async (name, email, password) => {
        const { data } = await api.post("/auth/register", { name, email, password });
        setUser(data);
        return data;
    }, []);

    const logout = useCallback(async () => {
        try {
            await api.post("/auth/logout");
        } catch (err) {
            console.error("[Auth] logout failed:", err);
        }
        setUser(false);
    }, []);

    const value = useMemo(
        () => ({ user, checked, login, register, logout, refresh, formatApiErrorDetail }),
        [user, checked, login, register, logout, refresh]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
